import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsWtSurveyEnabled } from '@/src/listings/bsp/config';
import {
  WT_SURVEY_BROADCAST_CHANNEL,
  WT_SURVEY_CLOSE_MESSAGE,
  WT_SURVEY_DWELL_MS,
  type WtSurveyDismissAction,
} from './constants';
import { isAutomation, isCrawlerUserAgent } from './environment';
import { markWtSurveySeen, shouldShowWtSurvey } from './storage';
import { trackWtSurveyClicked, trackWtSurveyDismissed, trackWtSurveyShown } from './tracking';

export type UseWtSurveyEligibilityResult = {
  isOpen: boolean;
  dismiss: (dismissAction: WtSurveyDismissAction) => void;
  click: () => void;
};

// No login gate and no backend call: for anonymous traffic localStorage is the
// key, so a server-side record would add no guarantee.
export const useWtSurveyEligibility = (): UseWtSurveyEligibilityResult => {
  const track = usePicoXTrack();
  const { isEnabled, isLoading: isFlagLoading } = useIsWtSurveyEnabled();

  const [isOpen, setIsOpen] = useState(false);
  // Guards against a repeat dismiss/click after close (overlapping pointer
  // events), which would emit the PicoX event twice.
  const isOpenRef = useRef(false);
  // Once a resolved gate says no, the decision is final — otherwise a later
  // state change (Orion settling, storage becoming readable) would reopen it.
  const decidedRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const closeModal = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);

  // Cross-tab dedup. A sibling that has not yet shown is latched off, so only
  // one tab ever shows it.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return undefined;
    }
    const channel = new BroadcastChannel(WT_SURVEY_BROADCAST_CHANNEL);
    channelRef.current = channel;
    const onMessage = (event: MessageEvent) => {
      if (event.data === WT_SURVEY_CLOSE_MESSAGE) {
        decidedRef.current = true;
        isOpenRef.current = false;
        setIsOpen(false);
      }
    };
    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
      channelRef.current = null;
    };
  }, []);

  const passesStaticGates = useCallback((): boolean => {
    if (typeof window === 'undefined' || decidedRef.current) {
      return false;
    }

    // Deployment-quality tests interact with listing surfaces; without this the
    // modal overlays them.
    if (isAutomation()) {
      decidedRef.current = true;
      return false;
    }

    if (isCrawlerUserAgent(typeof navigator !== 'undefined' ? navigator.userAgent : undefined)) {
      decidedRef.current = true;
      return false;
    }

    // Wait, do not latch: the flag defaults to false while loading, so deciding
    // now would permanently lock out anyone whose rule evaluates to true.
    if (isFlagLoading) {
      return false;
    }

    if (!isEnabled) {
      decidedRef.current = true;
      return false;
    }

    if (!shouldShowWtSurvey()) {
      decidedRef.current = true;
      return false;
    }

    return true;
  }, [isEnabled, isFlagLoading]);

  useEffect(() => {
    if (typeof document === 'undefined' || !passesStaticGates()) {
      return undefined;
    }

    let visibleSince: number | null = null;
    let accumulatedMs = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const show = () => {
      if (decidedRef.current) {
        return;
      }
      // A sibling tab may have written the record while this timer ran.
      if (!shouldShowWtSurvey()) {
        decidedRef.current = true;
        return;
      }
      decidedRef.current = true;
      markWtSurveySeen();
      isOpenRef.current = true;
      setIsOpen(true);
      trackWtSurveyShown(track);
      channelRef.current?.postMessage(WT_SURVEY_CLOSE_MESSAGE);
    };

    // Only visible time counts, so a tab left in the background does not
    // accumulate dwell.
    const startDwell = () => {
      if (timer !== null) {
        return;
      }
      visibleSince = Date.now();
      timer = setTimeout(
        () => {
          timer = null;
          if (visibleSince !== null) {
            accumulatedMs += Date.now() - visibleSince;
            visibleSince = null;
          }
          show();
        },
        Math.max(0, WT_SURVEY_DWELL_MS - accumulatedMs),
      );
    };

    const pauseDwell = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      if (visibleSince !== null) {
        accumulatedMs += Date.now() - visibleSince;
        visibleSince = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startDwell();
      } else {
        pauseDwell();
      }
    };

    if (document.visibilityState === 'visible') {
      startDwell();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      pauseDwell();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [passesStaticGates, track]);

  const dismiss = useCallback(
    (dismissAction: WtSurveyDismissAction) => {
      if (!isOpenRef.current) {
        return;
      }
      trackWtSurveyDismissed(track, dismissAction);
      channelRef.current?.postMessage(WT_SURVEY_CLOSE_MESSAGE);
      closeModal();
    },
    [track, closeModal],
  );

  const click = useCallback(() => {
    if (!isOpenRef.current) {
      return;
    }
    trackWtSurveyClicked(track);
    closeModal();
  }, [track, closeModal]);

  return { isOpen, dismiss, click };
};
